import React, { useState, useRef, useEffect } from "react";

/** Custom tooltip controlado por hover/focus.
 *
 * - Usa design tokens (--bg-1, --text, --border-strong, --accent) → coherente con la app.
 * - Soporta líneas múltiples (separadas por \n).
 * - Posiciona automáticamente (flip vertical si no cabe abajo).
 * - Sin dependencias (no Tailwind, no Radix).
 *
 * Uso:
 *   <InfoTooltip text="línea 1\nlínea 2" placement="bottom">
 *     <span>ⓘ</span>
 *   </InfoTooltip>
 */
export default function InfoTooltip({ children, text, placement = "bottom", maxWidth = 320, testId }) {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0, flip: false });
    const triggerRef = useRef(null);

    useEffect(() => {
        if (!open || !triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        // Estimar si cabe abajo (asumimos ~150px de altura del tooltip).
        const spaceBelow = window.innerHeight - rect.bottom;
        const flip = placement === "bottom" && spaceBelow < 160;
        setCoords({
            top: flip ? rect.top - 8 : rect.bottom + 8,
            left: rect.left + rect.width / 2,
            flip,
        });
    }, [open, placement]);

    const lines = String(text || "").split("\n");

    return (
        <>
            <span
                ref={triggerRef}
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => setOpen(false)}
                onFocus={() => setOpen(true)}
                onBlur={() => setOpen(false)}
                tabIndex={0}
                data-testid={testId}
                style={{ display: "inline-flex", alignItems: "center", cursor: "help" }}
            >
                {children}
            </span>
            {open && (
                <div
                    role="tooltip"
                    style={{
                        position: "fixed",
                        top: coords.top,
                        left: coords.left,
                        transform: coords.flip ? "translate(-50%, -100%)" : "translate(-50%, 0)",
                        zIndex: 9999,
                        maxWidth,
                        padding: "10px 12px",
                        background: "var(--bg-1)",
                        color: "var(--text)",
                        border: "1px solid var(--border-strong)",
                        borderRadius: 8,
                        boxShadow: "0 8px 24px -8px rgba(0,0,0,.45), 0 0 0 1px var(--accent) inset",
                        fontSize: 12,
                        lineHeight: 1.5,
                        whiteSpace: "normal",
                        textAlign: "left",
                        textTransform: "none",
                        letterSpacing: "normal",
                        pointerEvents: "none",
                    }}
                >
                    {lines.map((line, i) => (
                        <div key={i} style={{
                            marginBottom: i < lines.length - 1 ? 4 : 0,
                            opacity: line.startsWith("•") || line.startsWith("·") ? 0.9 : 1,
                            fontWeight: i === 0 ? 600 : 400,
                            color: i === 0 ? "var(--text)" : "var(--text-muted)",
                        }}>
                            {line || "\u00A0"}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}
